import { useState } from "react";
import { Check, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase-config";
import AuthRecoveryShell from "./components/auth/AuthRecoveryShell";

const ACTION_CODE_SETTINGS = {
  url: "https://harmonauria.fr/auth/login",
  handleCodeInApp: false,
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const cleanEmail = email.trim();

    if (!cleanEmail || !cleanEmail.includes("@")) {
      setError("Veuillez saisir une adresse email valide.");
      return;
    }

    try {
      setLoading(true);
      auth.languageCode = "fr";
      await sendPasswordResetEmail(auth, cleanEmail, ACTION_CODE_SETTINGS);
      setSuccess(true);
    } catch (error) {
      console.error("Password reset email error:", error);

      if (error?.code === "auth/user-not-found") {
        setSuccess(true);
        return;
      }

      if (error?.code === "auth/invalid-email") {
        setError("Cette adresse email n'est pas valide.");
        return;
      }

      if (error?.code === "auth/too-many-requests") {
        setError("Trop de demandes ont été envoyées. Réessayez dans quelques minutes.");
        return;
      }

      if (error?.code === "auth/network-request-failed") {
        setError("La connexion semble interrompue. Vérifiez votre réseau puis réessayez.");
        return;
      }

      setError("L'envoi du lien est momentanément indisponible. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthRecoveryShell
      eyebrow="Retrouver votre espace"
      title={success ? "Le lien est en chemin" : "Mot de passe oublié ?"}
      description={success ? "Nous avons préparé la suite pour que vous puissiez retrouver votre refuge en toute simplicité." : "Indiquez l'adresse liée à votre compte. Nous vous enverrons un lien sécurisé pour choisir un nouveau mot de passe."}
    >
      {!success ? (
        <form
          className="hrec-form"
          onSubmit={handleSubmit}
        >
          <div className="hrec-group">
            <label
              htmlFor="reset-email"
              className="hrec-label"
            >
              Adresse email
            </label>

            <div className="hrec-field">
              <Mail
                size={18}
                className="hrec-field-icon"
              />

              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="hrec-input"
                placeholder="votre@email.com"
                autoComplete="email"
                autoFocus
                required
              />
            </div>
          </div>

          {error && (
            <p
              className="hrec-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            className="hrec-submit"
            disabled={loading}
          >
            {loading && <span className="hrec-spinner" />}
            <span>{loading ? "Envoi du lien…" : "Recevoir le lien"}</span>
          </button>

          <p className="hrec-note">Par sécurité, le même message s'affiche même si aucun compte ne correspond à cette adresse.</p>
        </form>
      ) : (
        <div
          className="hrec-state"
          aria-live="polite"
        >
          <div className="hrec-state-icon">
            <Check
              size={26}
              strokeWidth={2.2}
            />
          </div>

          <h2 className="hrec-state-title">Consultez votre boîte mail</h2>

          <p className="hrec-state-text">Si un compte est associé à <strong>{email.trim()}</strong>, vous recevrez le lien dans quelques instants. Il peut parfois falloir une ou deux minutes.</p>

          <Link
            to="/auth/login"
            className="hrec-submit"
          >
            Retour à la connexion
          </Link>
        </div>
      )}
    </AuthRecoveryShell>
  );
}
